import View from '@play-co/timestep-core/lib/ui/View';
import bitmapFonts from 'src/lib/bitmapFonts';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';

// properties used in calculations
type Style = {
  width: number;
  height: number;
  padding: number;
  margin: number;
  minPadding: number;
  labelOffsetX: number;
  fontSize: number;
  selectedHeightOffset: number;
};

// default values, based on mockups
const defaults: Style = {
  width: 229,
  height: 51,
  padding: 124,
  margin: 0,
  minPadding: 48,
  labelOffsetX: -2,
  fontSize: 27,
  selectedHeightOffset: 4,
};

// properties from parent class, used to calculate width and position of tab button
type Markup = {
  offsetX: number;
  buttonsCount: number;
  style?: Partial<Style>;
};

type Opts = {
  superview: View;
  label: () => string;

  onSelect: () => void;
  selected?: boolean;
  markup: Markup;
  tracking: { feature: string; $subFeature: string };
};

export default class Tab {
  private button: ButtonScaleViewWithText;
  private style: Style;

  constructor(opts: Opts) {
    this.style = {
      ...defaults,
      ...opts.markup.style,
    };

    const { width, height, labelOffsetX, fontSize } = this.style;
    this.button = new ButtonScaleViewWithText({
      ...uiConfig.buttons.tab,
      superview: opts.superview,
      font: 'Body',
      localeText: opts.label,
      x: opts.markup.offsetX,
      width,
      height,
      labelOffsetX,
      fontSize,
      labelPaddingX: 24,
      onClick: async () => opts.onSelect(),
      tracking: opts.tracking,
    });

    this.reflow(opts.superview, opts.markup);

    this.setProps({ selected: opts.selected || false });
  }

  setProps(props: { selected: boolean }) {
    const { height, selectedHeightOffset } = this.style;
    if (props.selected) {
      this.button.setDisabled(true);
      this.button.updateOpts({
        zIndex: 1,
        offsetY: -selectedHeightOffset,
        height: height + selectedHeightOffset,
      });
    } else {
      this.button.setDisabled(false);
      this.button.updateOpts({
        zIndex: 0,
        offsetY: 0,
        height,
      });
    }
  }

  public updateOpts(opts: { x: number }) {
    this.button.updateOpts(opts);
  }

  getView() {
    return this.button;
  }

  destroy() {
    this.button.removeFromSuperview();
  }

  private reflow(superview: View, markup: Markup) {
    const { padding, margin, minPadding } = this.style;
    const spaceForMargin = margin * (markup.buttonsCount - 1);
    const minimumWidth =
      (superview.style.width - spaceForMargin) / markup.buttonsCount;
    const textWidth = Math.min(
      this.button.label.getTextWidth() + padding,
      minimumWidth,
    );

    this.button.updateOpts({
      width: textWidth,
    });

    this.button.label.updateOpts({
      x: this.button.style.width / 2,
      width: this.button.style.width - minPadding,
      centerOnOrigin: true,
    });
  }
}
